import React, { useEffect, useState, useCallback, useMemo } from "react";
import { getBidderByUuid } from "services";
import {
  findFirstPendingStep,
  determineKycType,
  getJointIndividualStepIndex,
  getStepTitles,
} from "./utils/kycStepUtils";
import { useRequiresApproverDetails } from "./utils/useRequiresApproverDetails";
import HorizontalStepper from "./HorizontalStepper";
import { useRouter } from "next/router";
import PaymentValidationApplicationForm from "./components/PaymentValidationApplicationForm";
import JointIndividualOnlinePrimaryKycStepContent from "./components/jointBidders/JointIndividualOnlinePrimaryKycStepContent";
import JointIndividualOnlineViewerKycStepContent from "./components/jointBidders/JointIndividualOnlineViewerKycStepContent";
import JointIndividualOfflinePrimaryKycStepContent from "./components/jointBidders/JointIndividualOfflinePrimaryKycStepContent";
import JointIndividualOfflineViewerKycStepContent from "./components/jointBidders/JointIndividualOfflineViewerKycStepContent";
import ClockIcon from "../../../../icons/ClockIcon";
import KYCBackButtonWithListingId from "./components/KYCBackButtonWithListingId";

interface JBIndividualKycWrapperProps {
  bidderUuid: any;
}

export default function JBIndividualKycWrapper({
  bidderUuid,
}: JBIndividualKycWrapperProps) {
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [bidder, setBidder] = useState<any>(null);
  const [activeStep, setActiveStep] = useState(-1);
  const [kycMode, setKycMode] = useState<any>("");
  const [kycStepEligible, setKycStepEligible] = useState<any>(false);
  const [offlinePaymentValidation, setOfflinePaymentValidation] =
    useState(false);
  const [offlinePaymentValidationState, setOfflinePaymentValidationState] =
    useState("pending");

  // Get step titles based on KYC mode and access type using useMemo
  const stepsTitles = useMemo(
    () =>
      getStepTitles(
        "joint_individual",
        kycMode || "online",
        bidder?.access_type || "primary",
      ),
    [kycMode, bidder?.access_type],
  );
  const requiresApproverDetails = useRequiresApproverDetails(bidder, kycMode);
  const primaryOfflineStepperTitles = useMemo(() => {
    if (requiresApproverDetails) {
      return stepsTitles;
    }
    return stepsTitles.filter((step) => step.value !== "approver_details");
  }, [requiresApproverDetails, stepsTitles]);
  const viewerOfflineStepperTitles = useMemo(() => {
    if (requiresApproverDetails) {
      return stepsTitles;
    }
    return stepsTitles.map((step) =>
      step.value === "approver_details"
        ? { label: "Final Submission", value: "final_submission" }
        : step,
    );
  }, [requiresApproverDetails, stepsTitles]);

  // Function to recalculate and set the correct step based on current bidder data
  const recalculateAndSetStep = useCallback(() => {
    if (!bidder || !kycMode) return;

    const kycType = determineKycType(bidder);
    const accessType = bidder?.access_type;
    const step = findFirstPendingStep(
      bidder?.meta?.steps_status,
      kycType,
      kycMode,
      accessType,
    );
    console.log("step: ", step);

    const calculatedStep = getJointIndividualStepIndex(
      step,
      accessType,
      kycMode,
    );
    console.log("calculatedStep: ", calculatedStep);

    // Only update if the calculated step is different from current activeStep
    if (calculatedStep !== activeStep && calculatedStep !== -1) {
      setActiveStep(calculatedStep);
    }
  }, [bidder, kycMode, activeStep]);

  const init = useCallback(async (bidderUuid: any) => {
    setLoading(true);
    if (bidderUuid) {
      try {
        console.log("bidderUuid in init:", bidderUuid);
        const data: any = await getBidderByUuid(bidderUuid);
        setBidder(data);
        const mode = data?.meta?.bid_application_flow_mode
          ? data?.meta?.bid_application_flow_mode
          : "online";
        if (
          process.env.NEXT_PUBLIC_ENABLE_OFFLINE_JOINT_INDIVIDUALS_KYC ===
          "true"
        ) {
          setKycMode(mode);
        } else {
          setKycMode("online");
        }

        if (
          process.env.NEXT_PUBLIC_ENABLE_ONLINE_DD_VALIDATION_FLOW === "true" &&
          data?.access_type === "primary"
        ) {
          if (data?.meta?.enable_offline_payment_validation === true) {
            setOfflinePaymentValidation(true);
            if (data?.payment_data?.form_submission !== "submitted") {
              setOfflinePaymentValidationState("pending");
            } else {
              setOfflinePaymentValidationState("submitted");
            }
          }
        }

        // Calculate initial step using the utility function
        if (mode === "offline" || mode === "online") {
          const kycType = determineKycType(data);
          const accessType = data?.access_type;
          if (accessType === "viewer") {
            const primaryBidderUuid: any = data?.meta?.primary_bidder_uuid;
            if (primaryBidderUuid) {
              const primaryBidderData: any = await getBidderByUuid(
                primaryBidderUuid,
              );
              if (
                primaryBidderData?.verification_step === "wait_for_consent" ||
                primaryBidderData?.verification_step === "completed"
              ) {
                setKycStepEligible(true);
              }
            }
          } else {
            setKycStepEligible(true);
          }
          const step = findFirstPendingStep(
            data?.meta?.steps_status,
            kycType,
            mode,
            accessType,
          );

          // Map to step index using helper function
          const calculatedStep = getJointIndividualStepIndex(
            step,
            accessType,
            mode,
          );
          setActiveStep(calculatedStep);
        }
      } catch (error) {
        console.log("error: ", error);
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    init(bidderUuid);
  }, [bidderUuid, init]);

  // Recalculate step when bidder data changes (after updates)
  useEffect(() => {
    if (bidder && kycMode && !loading && activeStep !== -1) {
      // Use a small delay to ensure state updates are complete
      const timeoutId = setTimeout(() => {
        recalculateAndSetStep();
      }, 50);
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bidder?.meta?.steps_status, kycMode, loading]);

  // Wrapper function for init that includes step recalculation
  const initWithRecalculation = useCallback(
    async (uuid?: any) => {
      await init(uuid || bidderUuid);
      recalculateAndSetStep();
    },
    [init, bidderUuid, recalculateAndSetStep],
  );

  if (
    offlinePaymentValidation === true &&
    offlinePaymentValidationState === "pending"
  ) {
    return (
      <PaymentValidationApplicationForm
        bidder={bidder}
        bidderUuid={bidderUuid}
        init={init}
        activeStep={activeStep}
        setActiveStep={setActiveStep}
      />
    );
  }
  if (!loading && kycStepEligible === false) {
    return (
      <div className="w-full max-w-2xl mx-auto py-6 md:py-8">
        <div className="border border-[#FFE5D9] bg-[#fff9f7] rounded-sm p-4">
          <div className="text-center">
            <div className="flex justify-center mb-2">
              <div className="bg-[#FFE5D9] p-3 rounded-full">
                <ClockIcon className="h-6 w-6 text-brand-orange" />
              </div>
            </div>
            <h4 className="text-sm md:text-lg font-semibold font-openSans text-brand-textBlack">
              KYC Step Not Eligible
            </h4>
            <p className="text-xs md:text-sm text-brand-textGrey font-openSans mb-3">
              Waiting for Primary Bidder
            </p>
            <div className="space-y-2 text-left">
              <div className="bg-white border border-[#FFE5D9] rounded-lg p-3">
                <p className="text-xs md:text-sm font-openSans text-brand-textBlack leading-relaxed">
                  You need to wait until the{" "}
                  <span className="font-semibold text-brand-orange">
                    Primary Bidder
                  </span>{" "}
                  completes their KYC process before you can proceed.
                </p>
              </div>
              <div className="bg-white border border-[#FFE5D9] rounded-lg p-3">
                <p className="text-xs md:text-sm font-openSans text-brand-textGrey leading-relaxed">
                  We recommend connecting with your Primary Bidder to complete
                  the process as soon as possible.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  const isJointIndividualOfflineFlowEnabled =
    process.env.NEXT_PUBLIC_ENABLE_OFFLINE_JOINT_INDIVIDUALS_KYC === "true";
  return (
    <div>
      <div>
        {loading ? (
          <div className="flex flex-col flex-grow h-full">
            <div className="flex justify-center items-center">
              <div className="w-12 h-12 border-8 border-t-6 border-brand-orange rounded-full animate-spin bg-gradient-to-r from-brand-orange to-blue-100 shadow-lg"></div>
            </div>
          </div>
        ) : (
          <>
            <KYCBackButtonWithListingId
              bidderAuctionId={bidder?.meta?.event_id}
            />
            {isJointIndividualOfflineFlowEnabled &&
              kycMode === "offline" &&
              bidder?.access_type === "primary" && (
                <div>
                  <HorizontalStepper
                    totalSteps={primaryOfflineStepperTitles.length}
                    activeStep={activeStep}
                    setActiveStep={setActiveStep}
                    stepsTitles={primaryOfflineStepperTitles}
                  />

                  <JointIndividualOfflinePrimaryKycStepContent
                    activeStep={activeStep}
                    bidderUuid={bidderUuid}
                    bidder={bidder}
                    setActiveStep={setActiveStep}
                    init={initWithRecalculation}
                    offlinePaymentValidation={offlinePaymentValidation}
                    stepsTitles={primaryOfflineStepperTitles}
                    requiresApproverDetails={requiresApproverDetails}
                  />
                </div>
              )}
            {isJointIndividualOfflineFlowEnabled &&
              kycMode === "offline" &&
              bidder?.access_type === "viewer" && (
                <div>
                  <HorizontalStepper
                    totalSteps={viewerOfflineStepperTitles.length}
                    activeStep={activeStep}
                    setActiveStep={setActiveStep}
                    stepsTitles={viewerOfflineStepperTitles}
                  />
                  <JointIndividualOfflineViewerKycStepContent
                    activeStep={activeStep}
                    bidderUuid={bidderUuid}
                    bidder={bidder}
                    setActiveStep={setActiveStep}
                    init={initWithRecalculation}
                    offlinePaymentValidation={offlinePaymentValidation}
                    stepsTitles={viewerOfflineStepperTitles}
                    requiresApproverDetails={requiresApproverDetails}
                  />
                </div>
              )}
            {kycMode === "online" && bidder?.access_type === "primary" && (
              <div>
                <HorizontalStepper
                  totalSteps={stepsTitles.length}
                  activeStep={activeStep}
                  setActiveStep={setActiveStep}
                  stepsTitles={stepsTitles}
                />
                <JointIndividualOnlinePrimaryKycStepContent
                  activeStep={activeStep}
                  bidderUuid={bidderUuid}
                  bidder={bidder}
                  setActiveStep={setActiveStep}
                  init={initWithRecalculation}
                  offlinePaymentValidation={offlinePaymentValidation}
                  stepsTitles={stepsTitles}
                />
              </div>
            )}
            {kycMode === "online" && bidder?.access_type === "viewer" && (
              <div>
                <HorizontalStepper
                  totalSteps={stepsTitles.length}
                  activeStep={activeStep}
                  setActiveStep={setActiveStep}
                  stepsTitles={stepsTitles}
                />
                <JointIndividualOnlineViewerKycStepContent
                  activeStep={activeStep}
                  bidderUuid={bidderUuid}
                  bidder={bidder}
                  setActiveStep={setActiveStep}
                  init={initWithRecalculation}
                  offlinePaymentValidation={offlinePaymentValidation}
                  stepsTitles={stepsTitles}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
